
import { useState, useEffect, useCallback } from 'react';

const galleryImages = [
  {
    src: "https://static.readdy.ai/image/b7cf4d15025d84086817049d392fc8bc/585f083a5125f0c8476d990fe1f826d4.jpeg",
    alt: "Happy travelers at beach"
  },
  {
    src: "https://static.readdy.ai/image/b7cf4d15025d84086817049d392fc8bc/42f34cf7e942a7706ea59c3aa996d091.jpeg",
    alt: "Friends at waterfalls"
  },
  {
    src: "https://static.readdy.ai/image/b7cf4d15025d84086817049d392fc8bc/2c270d7d7e00c15f79a4fc615049da2c.jpeg",
    alt: "Family at cable car"
  },
  {
    src: "https://static.readdy.ai/image/b7cf4d15025d84086817049d392fc8bc/936fb3217105ce3a6774adde1b06492a.jpeg",
    alt: "Couple on catamaran"
  },
  {
    src: "https://static.readdy.ai/image/b7cf4d15025d84086817049d392fc8bc/169d3c9f1e026420d5809419bbaf8d6c.jpeg",
    alt: "Snorkeling adventure"
  },
  {
    src: "https://static.readdy.ai/image/b7cf4d15025d84086817049d392fc8bc/c5c0d1cf4146385d8a31ee92017ac3f6.jpeg",
    alt: "Horseback riding"
  },
  {
    src: "https://static.readdy.ai/image/b7cf4d15025d84086817049d392fc8bc/1a2c4a2df79906b42bfdf20570658aeb.jpeg",
    alt: "Colonial zone tour"
  },
  {
    src: "https://static.readdy.ai/image/b7cf4d15025d84086817049d392fc8bc/b9f074de60f33dae3a94cd0a0896e6a2.jpeg",
    alt: "Whale watching"
  },
  {
    src: "https://static.readdy.ai/image/b7cf4d15025d84086817049d392fc8bc/60eddcbaacef81cd8de849e3df6a6a6b.jpeg",
    alt: "Zip line adventure"
  }
];

const GallerySlideshow = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [direction, setDirection] = useState<'left' | 'right'>('right');

  const nextSlide = useCallback(() => {
    setDirection('right');
    setActiveIndex((prev) => (prev + 1) % galleryImages.length);
  }, []);

  const prevSlide = useCallback(() => {
    setDirection('left');
    setActiveIndex((prev) => (prev - 1 + galleryImages.length) % galleryImages.length);
  }, []);

  const goToSlide = (index: number) => {
    setDirection(index > activeIndex ? 'right' : 'left');
    setActiveIndex(index);
  };

  useEffect(() => {
    if (!isAutoPlaying) return;
    const interval = setInterval(nextSlide, 4000);
    return () => clearInterval(interval);
  }, [isAutoPlaying, nextSlide]);

  const getVisibleImages = () => {
    const indices = [];
    for (let i = -2; i <= 2; i++) {
      const index = (activeIndex + i + galleryImages.length) % galleryImages.length;
      indices.push({ index, position: i });
    }
    return indices;
  };

  return (
    <div 
      className="relative"
      onMouseEnter={() => setIsAutoPlaying(false)}
      onMouseLeave={() => setIsAutoPlaying(true)}
    >
      {/* Main Carousel */}
      <div className="relative h-[500px] flex items-center justify-center perspective-1000">
        <div className="relative w-full h-full flex items-center justify-center">
          {getVisibleImages().map(({ index, position }) => {
            const isCenter = position === 0;
            const absPosition = Math.abs(position);
            
            return (
              <div
                key={`${index}-${position}`}
                className="absolute transition-all duration-700 ease-out cursor-pointer"
                style={{
                  transform: `
                    translateX(${position * 280}px) 
                    translateZ(${isCenter ? 100 : -absPosition * 100}px) 
                    rotateY(${position * -15}deg)
                    scale(${isCenter ? 1.1 : 1 - absPosition * 0.15})
                  `,
                  zIndex: 10 - absPosition,
                  opacity: 1 - absPosition * 0.25,
                  filter: isCenter ? 'none' : `blur(${absPosition * 1}px)`,
                }}
                onClick={() => goToSlide(index)}
              >
                <div 
                  className={`
                    relative overflow-hidden rounded-3xl shadow-2xl
                    ${isCenter ? 'w-80 h-96' : 'w-64 h-80'}
                    transition-all duration-700
                  `}
                >
                  <img
                    src={galleryImages[index].src}
                    alt={galleryImages[index].alt}
                    className={`
                      w-full h-full object-cover object-center
                      transition-transform duration-700
                      ${isCenter ? 'hover:scale-110' : ''}
                    `}
                  />
                  
                  {/* Glow effect for center image */}
                  {isCenter && (
                    <>
                      <div className="absolute inset-0 bg-gradient-to-t from-[#FF6B6B]/30 via-transparent to-[#0A2463]/20 opacity-0 hover:opacity-100 transition-opacity duration-500" />
                      <div className="absolute -inset-1 bg-gradient-to-r from-[#FF6B6B] via-[#FFD93D] to-[#FF6B6B] rounded-3xl opacity-0 hover:opacity-50 blur-xl transition-opacity duration-500 -z-10 animate-pulse" />
                    </>
                  )}
                  
                  {/* Reflection effect */}
                  <div className="absolute inset-0 bg-gradient-to-br from-white/20 via-transparent to-transparent pointer-events-none" />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Navigation Arrows */}
      <button
        onClick={prevSlide}
        className="absolute left-4 top-1/2 -translate-y-1/2 w-14 h-14 bg-white/90 backdrop-blur-sm rounded-full shadow-xl flex items-center justify-center text-[#0A2463] hover:bg-[#FF6B6B] hover:text-white transition-all duration-300 hover:scale-110 z-20 cursor-pointer group"
      >
        <i className="ri-arrow-left-s-line text-2xl group-hover:animate-pulse"></i>
      </button>
      <button
        onClick={nextSlide}
        className="absolute right-4 top-1/2 -translate-y-1/2 w-14 h-14 bg-white/90 backdrop-blur-sm rounded-full shadow-xl flex items-center justify-center text-[#0A2463] hover:bg-[#FF6B6B] hover:text-white transition-all duration-300 hover:scale-110 z-20 cursor-pointer group"
      >
        <i className="ri-arrow-right-s-line text-2xl group-hover:animate-pulse"></i>
      </button>

      {/* Dots Navigation */}
      <div className="flex justify-center gap-3 mt-8">
        {galleryImages.map((_, index) => (
          <button
            key={index}
            onClick={() => goToSlide(index)}
            className={`
              transition-all duration-500 cursor-pointer
              ${activeIndex === index 
                ? 'w-10 h-3 bg-gradient-to-r from-[#FF6B6B] to-[#FFD93D] rounded-full shadow-lg shadow-[#FF6B6B]/50' 
                : 'w-3 h-3 bg-gray-300 rounded-full hover:bg-[#FF6B6B]/50'
              }
            `}
          />
        ))}
      </div>

      {/* Floating particles effect */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        {[...Array(6)].map((_, i) => (
          <div
            key={i}
            className="absolute w-2 h-2 bg-[#FFD93D]/40 rounded-full animate-float"
            style={{
              left: `${15 + i * 15}%`,
              top: `${20 + (i % 3) * 25}%`,
              animationDelay: `${i * 0.5}s`,
              animationDuration: `${3 + i * 0.5}s`,
            }}
          />
        ))}
      </div>

      <style>{`
        .perspective-1000 {
          perspective: 1500px;
        }
        
        @keyframes float {
          0%, 100% {
            transform: translateY(0) rotate(0deg);
            opacity: 0.4;
          }
          50% {
            transform: translateY(-30px) rotate(180deg);
            opacity: 0.8;
          }
        }
        
        .animate-float {
          animation: float 4s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
};

export default GallerySlideshow;
